import { FetchResult, useMutation } from '@apollo/client';
import { TTranslator } from '@smartshell/ui-kit';

import { TLocalesKeys } from 'localization';
import {
  CHANGE_ADVERTISEMENT_STATUS,
  CREATE_ADVERTISEMENT,
  DELETE_ADVERTISEMENT,
  GET_ADVERTISEMENTS,
  GET_MY_ADVERTISEMENTS,
  INCREMENT_PARTNER_COUNTER,
  REPUBLISH_ADVERTISEMENT,
  UPDATE_ADVERTISEMENT,
} from 'schemas';
import notification from 'services/notificationService';
import { EAdvertisementStatus } from 'types';

import {
  IAdvertisementInput,
  IUpdateAdvertisementInput,
  TChangeStatusResult,
  TCreateAdvertisementResult,
  TDeleteAdvertisementResult,
  TRepublishResult,
  TUpdateAdvertisementResult,
  UseAdvertisementMutationsReturn,
} from './interfaces';

export const useAdvertisementMutations = (
  t: TTranslator<TLocalesKeys>
): UseAdvertisementMutationsReturn => {
  const [createAdvertisement, createState] = useMutation(CREATE_ADVERTISEMENT, {
    refetchQueries: [GET_MY_ADVERTISEMENTS],
    onError: (error: Error) => {
      return notification.info({
        t,
        type: 'error',
        title: t('errors.error'),
        message: error?.message,
        dismiss: 5,
      });
    },
  });

  const [updateAdvertisement, updateState] = useMutation(UPDATE_ADVERTISEMENT, {
    refetchQueries: [GET_MY_ADVERTISEMENTS],
    onError: (error: Error) => {
      return notification.info({
        t,
        type: 'error',
        title: t('errors.error'),
        message: error?.message,
        dismiss: 5,
      });
    },
  });

  const [deleteAdvertisement, deleteState] = useMutation(DELETE_ADVERTISEMENT, {
    refetchQueries: [GET_MY_ADVERTISEMENTS],
    onCompleted: (data) => {
      return notification.info({
        t,
        type: 'success',
        title: t('advertisement.delete.success'),
        message: data?.deleteAdvertisement?.title,
        dismiss: 5,
      });
    },
    onError: (error: Error) => {
      return notification.info({
        t,
        type: 'error',
        title: t('errors.error'),
        message: error?.message,
        dismiss: 5,
      });
    },
  });

  const [changeStatus, changeState] = useMutation(CHANGE_ADVERTISEMENT_STATUS, {
    refetchQueries: [GET_MY_ADVERTISEMENTS, GET_ADVERTISEMENTS],
    onCompleted: (data) => {
      return notification.info({
        t,
        type: 'success',
        title: t('advertisement.unpublished.success'),
        message: data?.changeAdvertisementStatus?.title,
        dismiss: 5,
      });
    },
    onError: (error: Error) => {
      return notification.info({
        t,
        type: 'error',
        title: t('errors.error'),
        message: error?.message,
        dismiss: 5,
      });
    },
  });

  const [republishAdvertisement, republishState] = useMutation(
    REPUBLISH_ADVERTISEMENT,
    {
      refetchQueries: [GET_MY_ADVERTISEMENTS],
      onCompleted: (data) => {
        return notification.info({
          t,
          type: 'success',
          title: t('advertisement.republished.success'),
          message: data?.republishAdvertisement?.title,
          dismiss: 5,
        });
      },
      onError: (error: Error) => {
        return notification.info({
          t,
          type: 'error',
          title: t('errors.error'),
          message: error?.message,
          dismiss: 5,
        });
      },
    }
  );

  const [incrementPartnerCounter] = useMutation(INCREMENT_PARTNER_COUNTER);

  const handledIncrementPartnerCounter = (
    id: number
  ): Promise<FetchResult<unknown>> =>
    incrementPartnerCounter({ variables: { id } });

  const handledCreateAdvertisement = (
    input: IAdvertisementInput
  ): Promise<FetchResult<TCreateAdvertisementResult>> =>
    createAdvertisement({ variables: { input } });

  const handledUpdateAdvertisement = (
    id: string,
    input: IUpdateAdvertisementInput
  ): Promise<FetchResult<TUpdateAdvertisementResult>> =>
    updateAdvertisement({ variables: { id, input } });

  const handledDeleteAdvertisement = (
    id: string
  ): Promise<FetchResult<TDeleteAdvertisementResult>> =>
    deleteAdvertisement({ variables: { id } });

  const handledChangeAdvertisementStatus = (
    id: string,
    status: EAdvertisementStatus
  ): Promise<FetchResult<TChangeStatusResult>> =>
    changeStatus({ variables: { id, status } });

  const handledRepublishAdvertisement = (
    id: string
  ): Promise<FetchResult<TRepublishResult>> =>
    republishAdvertisement({ variables: { id } });

  return {
    handledCreateAdvertisement,
    handledUpdateAdvertisement,
    handledDeleteAdvertisement,
    handledChangeAdvertisementStatus,
    handledRepublishAdvertisement,
    handledIncrementPartnerCounter,

    creating: createState.loading,
    updating: updateState.loading,
    deleting: deleteState.loading,
    changingStatus: changeState.loading,
    republishing: republishState.loading,
  };
};
